import AlbInput from "@components/common/AlbInput";
import { Autocomplete, Divider, Grid, TextField, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import type { FieldErrors } from "react-hook-form";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAutocompleteSuggestions } from "@/hooks/useAddressAutoComplete";
import type { AddressSchemaType } from "@/schemas/common/address";

type TAddress = {
  title: string;
  identifier:
    | "toAddress"
    | "fromAddress"
    | "toReturnFromAddress"
    | "toReturnToAddress"
    | `${string}.address`;
  disabled?: boolean;
};

function Address({ title, identifier, disabled = false }: TAddress) {
  const { field } = useController({
    name: `${identifier}.address`,
  });

  const {
    setValue,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();

  const { suggestions, resetSession, isLoading } = useAutocompleteSuggestions(field.value || "");

  const predictions = useMemo(
    () =>
      suggestions
        .filter((suggestion) => suggestion.placePrediction)
        .map(({ placePrediction }) => placePrediction),
    [suggestions]
  );

  const handleInputChange = useCallback(
    (value: google.maps.places.PlacePrediction | string) => {
      if (typeof value === "string") {
        field.onChange(value);
      }
    },
    [field]
  );

  const handleSelect = useCallback(
    (prediction: google.maps.places.PlacePrediction | string) => {
      if (typeof prediction === "string") {
        return;
      }
      const address = prediction?.text.text;
      const place = prediction.toPlace();
      place
        .fetchFields({
          fields: ["location", "addressComponents"],
        })
        .then(() => {
          resetSession();
          const addressComponents = place.addressComponents;
          if (!addressComponents) {
            return;
          }
          const city = addressComponents.find((component) =>
            component.types.includes("locality")
          )?.longText;
          const postalCode = addressComponents.find((component) =>
            component.types.includes("postal_code")
          )?.longText;
          const country = addressComponents.find((component) =>
            component.types.includes("country")
          )?.shortText;
          const longitude = place.location?.lng();
          const latitude = place.location?.lat();
          const placeId = place.id;

          setValue(`${identifier}.longitude`, longitude);
          setValue(`${identifier}.latitude`, latitude);
          setValue(`${identifier}.place_id`, placeId);
          setValue(`${identifier}.postalCode`, postalCode);
          setValue(`${identifier}.city`, city);
          setValue(`${identifier}.country`, country);
          setValue(`${identifier}.address`, address);
        })
        .catch(() => {});
    },
    [resetSession, setValue, identifier]
  );

  // --- Start Error Handling Logic ---
  const fieldError = errors[identifier] as FieldErrors<AddressSchemaType> | undefined;
  const getErrorMessageKey = () => {
    if (!fieldError) return null;
    // Use more specific keys from previous logic where appropriate,
    // otherwise use Zod's message (which should be 'required')
    if (fieldError.latitude || fieldError.longitude) return "coordinatesRequired";
    if (fieldError.city) return fieldError.city.message;
    if (fieldError.postalCode) return fieldError.postalCode.message;
    if (fieldError.country) return "countryRequired"; // Assuming Zod default isn't specific enough
    if (fieldError.address) return fieldError.address.message;
    // Add place_id check if necessary
    if (fieldError.place_id) return "placeIdRequired"; // Assuming a translation key exists
    return null;
  };
  const errorMessageKey = getErrorMessageKey();
  const hasError = Boolean(errorMessageKey);
  // --- End Error Handling Logic ---

  return (
    <Grid container direction="row" spacing={2} paddingBottom={2}>
      <Grid size={{ xs: 12 }}>
        <Typography variant="h5" textTransform="capitalize">
          {title}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Divider sx={{ bgcolor: "#fe5d8d", marginBottom: 3 }} />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Autocomplete
          getOptionLabel={(option) => {
            return typeof option === "string" ? option : option?.text?.text;
          }}
          disabled={disabled}
          freeSolo
          loading={isLoading}
          disableClearable
          filterOptions={(x) => x}
          options={predictions}
          onChange={(_event, searchedAddress) => {
            handleSelect(searchedAddress);
          }}
          value={field.value || ""}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                error={hasError}
                helperText={errorMessageKey ? t(`common.form.errors.${errorMessageKey}`) : ""}
                sx={{
                  textTransform: "capitalize",
                }}
                variant="outlined"
                label={t("common.form.input.search-an-address")}
                InputLabelProps={field.value ? { shrink: true } : {}}
                onChange={(event) => {
                  handleInputChange(event.target.value);
                }}
                InputProps={{
                  ...params.InputProps,
                  type: "search",
                }}
                placeholder="123 rue de Paris, 75001 Paris"
              />
            );
          }}
        />
      </Grid>
      <Grid size={{ xs: 4 }}>
        <AlbInput
          name={`${identifier}.city`}
          label={t("common.form.input.city")}
          type="TextField"
          isEditAllowed={false}
        />
      </Grid>
      <Grid size={{ xs: 4 }}>
        <AlbInput
          name={`${identifier}.postalCode`}
          label={t("common.form.input.zip-code")}
          type="TextField"
          isEditAllowed={false}
        />
      </Grid>
      <Grid size={{ xs: 4 }}>
        <AlbInput
          name={`${identifier}.country`}
          label={t("common.form.input.country")}
          type="TextField"
          isEditAllowed={false}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <AlbInput
          name={`${identifier}.addressPrecision`}
          label={t("common.form.input.address-accuracy")}
          type="TextField"
          isEditAllowed={!disabled}
        />
      </Grid>
    </Grid>
  );
}

export default Address;
